import { useState } from 'react';

import { useSendFamilyInviteMutation } from '@backrs_ui/grow-ui/src/redux/family/family.api';
import { SendFamilyInvitePayload } from '@backrs_ui/grow-ui/src/redux/family/family.types';

import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';

import { InviteMembersFormField } from './InviteMembersFormField';

type InviteFormData = Readonly<{
  invites: {
    first_name: string;
    email: string;
    role?: string;
  }[];
}>;

type InviteMembersFormProps = Readonly<{
  availableSeats: number;
}>;

export const InviteMembersForm = ({ availableSeats }: InviteMembersFormProps) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [sendFamilyInvite, { isLoading }] = useSendFamilyInviteMutation();
  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      invites: [{ first_name: '', email: '', role: undefined }],
    },
  });

  const onSubmit: SubmitHandler<InviteFormData> = async (values) => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    const nonEmptyInvites = values.invites.filter(
      (invite) => invite.first_name.trim() || invite.email.trim() || invite.role?.trim(),
    );

    // If no valid invites, don't submit
    if (nonEmptyInvites.length === 0) {
      return;
    }

    const payload: SendFamilyInvitePayload[] = nonEmptyInvites.map((invite) => ({
      email: invite.email,
      first_name: invite.first_name,
      family_role: invite.role,
    }));

    try {
      await sendFamilyInvite(payload).unwrap();
      setShowSuccessMessage(true);
      methods.reset({
        invites: [{ first_name: '', email: '', role: undefined }],
      });
    } catch (error) {
      console.error(error);
      setShowErrorMessage(true);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <InviteMembersFormField
          isLoading={isLoading}
          availableSeats={availableSeats}
          showSuccessMessage={showSuccessMessage}
          setShowSuccessMessage={setShowSuccessMessage}
          showErrorMessage={showErrorMessage}
          setShowErrorMessage={setShowErrorMessage}
        />
      </form>
    </FormProvider>
  );
};
